/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Button,
  SvgIcon,
  SpanWithData
} from "../"

import {
  showAsCurrency,
  decodeBase64
} from '../../utils'

import "./DeductionIdentityPayments.css"

class DeductionIdentityPayments extends React.PureComponent {
  static propTypes = {
    identityId: PropTypes.string.isRequired,
    deductionId: PropTypes.string.isRequired,
    removedPayments: PropTypes.object.isRequired,
    filter: PropTypes.string,
    search: PropTypes.string,
    onEditClick: PropTypes.func,
    readOnly: PropTypes.bool,
    errorOccurredDuringPaymentSave: PropTypes.bool,
    data: PropTypes.object.isRequired
  }

  constructor(props) {
    super(props)

    this.state = {
      identityPayments: [],
      removedPayments: null,
      creditAndInvestmentPayments: null,
      funeralCoverClientPayments: null,
      funeralCoverEmployerPayments: null,
      selected: false,
      errorOccurredDuringPaymentSave: props.errorOccurredDuringPaymentSave
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.payments) {
      if (nextProps.removedPayments) {
        if (nextProps.filter === '') {
          this.setState({
            identityPayments: [
              ...this.getCreditAndInvestmentPayments(nextProps.data.payments.edges),
              ...this.getFuneralCoverPayments(nextProps.data.payments.edges),
              ...this.getSolutionFeePayments(nextProps.data.payments.edges),
              ...this.getEarnBacksFeePayments(nextProps.data.payments.edges),
              ...this.getRemovedPayments(nextProps.identityId, nextProps.removedPayments)
            ]
          })
        } else {
          this.setState({
            identityPayments: [
              ...this.getCreditAndInvestmentPayments(nextProps.data.payments.edges),
              ...this.getFuneralCoverPayments(nextProps.data.payments.edges),
              ...this.getSolutionFeePayments(nextProps.data.payments.edges),
              ...this.getEarnBacksFeePayments(nextProps.data.payments.edges)
            ]
          })
        }
      } else {
        this.setState({
          identityPayments: [
            ...this.getCreditAndInvestmentPayments(nextProps.data.payments.edges),
            ...this.getFuneralCoverPayments(nextProps.data.payments.edges),
            ...this.getSolutionFeePayments(nextProps.data.payments.edges),
            ...this.getEarnBacksFeePayments(nextProps.data.payments.edges)
          ]
        })
      }
    } else {  // filter === 'removed'
      this.setState({ identityPayments: this.getRemovedPayments(nextProps.identityId, nextProps.removedPayments) })
    }
  }

  getSolutionFeePayments = (payments) => (
    payments.filter(payment => payment.node.prod.family === 'SOLUTION')
  )

  getEarnBacksFeePayments = (payments) => (
    payments.filter(payment => payment.node.prod.family === 'REWARDS')
  )

  getCreditAndInvestmentPayments = (payments) => (
    payments.filter(payment => (
      ['CREDIT', 'INVESTMENTS'].indexOf(payment.node.prod.family) > -1
    ))
  )

  getFuneralCoverPayments = (payments) => {
    let funeralCoverPayments = []
    let funeralCoverClientPayments = payments.filter(payment => (
      payment.node.prod.family === 'INSURANCE' && payment.node.source === 'CLIENT'
    ))
    let funeralCoverEmployerPayments = payments.filter(payment => (
      payment.node.prod.family === 'INSURANCE' && payment.node.source === 'EMPLOYER'
    ))
    // get sum of amounts across all payments
    let totalClientPaymentAmount = funeralCoverClientPayments.reduce((prev, cur) => prev + cur.node.amount, 0)
    let totalEmployerPaymentAmount = funeralCoverEmployerPayments.reduce((prev, cur) => prev + cur.node.amount, 0)

    if (funeralCoverClientPayments.length > 0) {
      let payment = funeralCoverClientPayments[0].node
      funeralCoverPayments.push(
        {
          node: {
            id: null,
            amount: totalClientPaymentAmount,
            prod: {
              family: payment.prod.family,
              name: payment.prod.name
            },
            source: 'CLIENT',
            state: payment.state,
            identityFor: {
              id: payment.identityFor.id,
              firstName: payment.identityFor.firstName,
              lastName: payment.identityFor.lastName,
              identityValue: payment.identityFor.identityValue,
              employeeNumber: payment.identityFor.client && payment.identityFor.client.employeeNumber
                ? payment.identityFor.client.employeeNumber
                : '-'
            }
          }
        }
      )
    }
    if (funeralCoverEmployerPayments.length > 0) {
      let payment = funeralCoverEmployerPayments[0].node
      funeralCoverPayments.push(
        {
          node: {
            id: null,
            amount: totalEmployerPaymentAmount,
            prod: {
              family: payment.prod.family,
              name: payment.prod.name
            },
            source: 'EMPLOYER',
            state: payment.state,
            identityFor: {
              id: payment.identityFor.id,
              firstName: payment.identityFor.firstName,
              lastName: payment.identityFor.lastName,
              identityValue: payment.identityFor.identityValue,
              employeeNumber: payment.identityFor.client && payment.identityFor.client.employeeNumber
                ? payment.identityFor.client.employeeNumber
                : '-'
            }
          }
        }
      )
    }

    return funeralCoverPayments
  }

  getRemovedPayments = (identity, removedPaymentsObj) => {
    let identityId = decodeBase64(identity)

    // if identity is found in removedPayments, store to display with payment records
    if (removedPaymentsObj.hasOwnProperty(identityId)) {
      let identityFor = {
        id: identity,
        firstName: removedPaymentsObj[identityId].first_name,
        lastName: removedPaymentsObj[identityId].last_name,
        identityValue: removedPaymentsObj[identityId].identity_value,
        employeeNumber: removedPaymentsObj[identityId].employee_number
      }
      let removedPayments = removedPaymentsObj[identityId].payments.map((payment) => (
        {
          node: {
            id: null,
            amount: payment.amount,
            product: payment.product,
            source: payment.source,
            state: "REMOVED",
            identityFor
          }
        }
      ))
      return removedPayments
    } else {
      return []
    }
  }

  render() {
    if (this.props.data.loading) {
      return (
        <tr className="stencilTableRow">
          <td><div /></td>
          <td><div /></td>
          <td><div /></td>
          <td><div /></td>
          <td><div /></td>
          <td><div /></td>
          <td><div /></td>
          <td><div /></td>
          <td />
        </tr>
      )
    }

    if (this.props.data.error) {
      return (
        <tr className="slds-border_top">
          <td colSpan={9}>
            Payment information could not be retrieved.
          </td>
        </tr>
      )
    }

    let payments = this.state.identityPayments
    .filter((payment) => (
      payment.node.transactedIn !== null
    ))
    .map((payment, index) => {
      let product = payment.node.prod
        ? payment.node.prod.name
        : "Unknown"
      let paidBy = `${payment.node.state === "REMOVED" ? payment.node.source.charAt(0).toUpperCase() : payment.node.source.charAt(0)}${payment.node.source.slice(1).toLowerCase()}`
      let identityFor = payment.node.identityFor

      let paymentState = payment.node.state === "REMOVED"
        ? "Removed"
        : payment.node.state === 'UNCHANGED'
          ? '-'
          : <SpanWithData
              forField="PaymentState"
              searchKey={payment.node.state}
              loadingComponent={<div style={{ padding: 2, backgroundColor: "lightgrey" }} />}
            />

      let editButton = index === 0 &&
        this.props.data.payments && this.props.data.payments.edges.length > 0  // does identity have current pending deduction payments? (won't have if terminated & have removed payments)
        ? <Button
            type="reset"
            disabled={this.props.readOnly}
            onClick={() => this.props.onEditClick(identityFor.id)}>
            <SvgIcon
              variant="utility"
              symbol="edit"
              size="x-small"
              buttonIcon={true}
              colour={this.props.readOnly ? "light" : "default"}
            />
          </Button>
        : ''

      return (
        <tr
          className={`${index === 0 ? "slds-border_top" : ''}${this.props.identityViewed === identityFor.id ? ` deduction-payments__viewed${this.props.errorOccurredDuringPaymentSave ? '_error' : ''}` : ''}`}
          key={`${identityFor.id}_payment_${index}`}>
          <td data-label="Name">
            {index === 0 ? identityFor.firstName : ''}
          </td>
          <td data-label="Surname">
            {index === 0 ? identityFor.lastName : ''}
          </td>
          <td data-label="ID">
            {index === 0 ? identityFor.identityValue : ''}
          </td>
          <td data-label="Empl#">
            {index === 0
              ? identityFor.employee
                ? identityFor.employee.employeeNumber : null
              : ''
            }
          </td>
          <td data-label="Product">
            {product}
          </td>
          <td data-label="Paid by">
            {paidBy === "Client" ? "Self" : paidBy}
          </td>
          <td data-label="Amount">
            {showAsCurrency('R', payment.node.amount, 2)}
          </td>
          <td data-label="State">
            {paymentState}
          </td>
          <td data-label=''>
            {editButton}
          </td>
        </tr>
      )
    })
    // payments as an array of tr's can now be returned below when using React v16
    return payments
  }
}

const getWorkflowRunIdentityPayments = gql`
  query ($identity: UUID, $deduction: UUID, $filter: String!, $search: String!, $listRemovedPaymentsOnly: Boolean!) {
    payments(identityFor_Id: $identity, workflowrunDeducting_Id: $deduction, state: $filter, identityFor_IdentityValue_Icontains: $search, orderBy: "identity_for__last_name") @skip(if: $listRemovedPaymentsOnly){
      edges {
        node {
          id
          prod {
            id
            name
            family
          }
          amount
          state
          status
          source
          editable
          identityFor {
            id
            firstName
            lastName
            identityValue
            client {
              id
              employeeNumber
            }
          }
          workflowrunDeducting {
            id
            parameters
          }
          transactedIn {
            id
          }
        }
      }
    }
  }
`

const withData =
  graphql(getWorkflowRunIdentityPayments, {
    options: ({ deductionId, identityId, filter, search }) => ({
      variables: {
        deduction: decodeBase64(deductionId),
        identity: decodeBase64(identityId),
        filter,
        search: search || '',
        listRemovedPaymentsOnly: filter === 'removed'
      },
      notifyOnNetworkStatusChange: true
    })
  })

export default withData(DeductionIdentityPayments)



// WEBPACK FOOTER //
// ./src/components/deduction-identity-payments/index.js